import { defineStore } from 'pinia';
import { computed, markRaw, Ref } from 'vue';
import partition from 'lodash/partition';
import sortBy from 'lodash/sortBy';
import { ServicesBuilder } from '../utils/setup';
import {
  ISection,
  Section,
  SectionService,
  SectionItemService,
  ISectionItem,
  SectionItem,
  InvoiceService,
  IInvoice,
  AppMiscService,
  ISectionItemService,
  ISectionService,
  GetManyResult,
} from '@posx/core';
import { Subscription } from 'dexie';
import { useMiscStore } from './misc.store';
import { logLiveQueryError, isLiveQueryCursorError } from '@/utils/logger';

interface SortSectionItemsOptions {
  currentItem: Ref<ISectionItem | null | undefined>;
  filter?: (item: ISectionItem) => boolean;
}

// Sort section items: current section (by distance) → other sections (by sequence, row/col)
export function useSortedSectionItems({ currentItem, filter }: SortSectionItemsOptions) {
  const sectionStore = useSectionStore();
  const seqMap = computed(() => new Map(sectionStore.sections.map(s => [s.uid, s.sequence ?? 999])));
  const sortedItems = computed(() => {
    const c = currentItem.value, cRow = c?.row || 0, cCol = c?.col || 0;
    const dist = (t: ISectionItem) => ((t.row || 0) - cRow) ** 2 + ((t.col || 0) - cCol) ** 2;
    const list = sectionStore.sectionItems?.filter(t => t.uid !== c?.uid && !t.deleted_at && (!filter || filter(t))) || [];
    const [same, other] = partition(list, t => t.section_uid === c?.section_uid);
    return [...sortBy(same, dist), ...sortBy(other, [t => seqMap.value.get(t.section_uid) ?? 999, 'row', 'col'])];
  });
  const getSectionName = (uid: string) => sectionStore.sections.find(s => s.uid === uid && !s.deleted_at)?.name || '';
  return { sortedItems, getSectionName };
}

export type SectionItemUnion = {
  [key: string]: ISectionItem[];
};

export interface SectionState {
  list: ISection[];
  currentSectionItem: ISectionItem;
  sectionItemMap: SectionItemUnion;
  sectionItems: ISectionItem[];
  sections: ISection[];
  sectionItemService: ISectionItemService | null;
  sectionService: ISectionService | null;
  _sectionSubscription: Subscription | null;
  _sectionItemSubscription: Subscription | null;
  _currentSectionItemSubscription: Subscription | null;
}

export const useSectionStore = defineStore('section', {
  state: (): SectionState => ({
    list: [],
    currentSectionItem: new SectionItem(),
    sectionItemMap: {},
    sectionItems: [],
    sections: [],
    sectionItemService: null as ISectionItemService | null,
    sectionService: null as ISectionService | null,
    _sectionSubscription: null as Subscription | null,
    _sectionItemSubscription: null as Subscription | null,
    _currentSectionItemSubscription: null as Subscription | null,
  }),
  actions: {
    async getSectionService(): Promise<ISectionService> {
      if (this.sectionService) {
        return this.sectionService;
      }
      // service singleton is method-only, skip deep proxy
      const service = markRaw(await ServicesBuilder.getInstance().getService<ISectionService>(SectionService));
      this.sectionService = service;
      return service;
    },
    async getSectionItemService(getNewInstance: boolean = false): Promise<ISectionItemService> {
      if (this.sectionItemService && !getNewInstance) {
        return this.sectionItemService;
      }
      // service singleton is method-only, skip deep proxy
      const service = markRaw(await ServicesBuilder.getInstance().getService<ISectionItemService>(SectionItemService));
      if(!getNewInstance) this.sectionItemService = service;
      return service;
    },
    async startSectionItemLiveQuery() {
      if (this._sectionItemSubscription) return;
      const service = await this.getSectionItemService();
      const sectionItemSub: Subscription = service
        .liveQuery(() => service.getMany({}, { pageSize: -1 }))
        .subscribe({
          next: (result: GetManyResult<ISectionItem>) => {
            this.sectionItems = result.list || [];
            this.updateSectionItemMap();
          },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'section', query: 'sectionItem' }); if (isLiveQueryCursorError(err) && this._sectionItemSubscription === sectionItemSub) { sectionItemSub.unsubscribe(); this._sectionItemSubscription = null; } },
        });
      this._sectionItemSubscription = markRaw(sectionItemSub);
    },
    async startSectionLiveQuery() {
      if (this._sectionSubscription) return;
      const service = await this.getSectionService();
      const sectionSub: Subscription = service
        .liveQuery(() => service.getMany({}, { pageSize: -1 }))
        .subscribe({
          next: (result: GetManyResult<ISection>) => {
            this.sections = result.list.sort((a: ISection, b: ISection) => a.sequence - b.sequence) || [];
            this.updateSectionItemMap();
          },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'section', query: 'section' }); if (isLiveQueryCursorError(err) && this._sectionSubscription === sectionSub) { sectionSub.unsubscribe(); this._sectionSubscription = null; } },
        });
      this._sectionSubscription = markRaw(sectionSub);
    },
    async startCurrentSectionItemLiveQuery(uid: string) {
      if (this._currentSectionItemSubscription && this.currentSectionItem?.uid !== uid) {
        this._currentSectionItemSubscription.unsubscribe();
        this._currentSectionItemSubscription = null;
      }
      if (this._currentSectionItemSubscription) return;
      const service = await this.getSectionItemService();
      const currentItemSub: Subscription = service
        .liveQuery(() => service.getOne(uid))
        .subscribe({
          next: (result: ISectionItem) => { this.currentSectionItem = result; },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'section', query: 'currentSectionItem', uid }); if (isLiveQueryCursorError(err) && this._currentSectionItemSubscription === currentItemSub) { currentItemSub.unsubscribe(); this._currentSectionItemSubscription = null; } },
        });
      this._currentSectionItemSubscription = markRaw(currentItemSub);
    },
    stopCurrentSectionItemLiveQuery() {
      if (this._currentSectionItemSubscription) {
        this._currentSectionItemSubscription.unsubscribe();
        this._currentSectionItemSubscription = null;
      }
    },
    stopSectionLiveQuery() {
      this._sectionSubscription?.unsubscribe();
      this._sectionSubscription = null;
    },
    stopSectionItemLiveQuery() {
      this._sectionItemSubscription?.unsubscribe();
      this._sectionItemSubscription = null;
    },
    stopAllLiveQueries() {
      this.stopSectionLiveQuery();
      this.stopSectionItemLiveQuery();
      this.stopCurrentSectionItemLiveQuery();
    },
    updateSectionItemMap() {
      this.sectionItemMap = this.sectionItems.reduce((acc, item) => {
        (acc[item.section_uid] = acc[item.section_uid] || []).push(item);
        return acc;
      }, {} as SectionItemUnion);
    },
    async getList() {
      const service = await this.getSectionService();
      const result = await service.getMany(
        {},
        {
          pageSize: -1,
        },
      );
      this.list = result?.list || [];

      return this.list;
    },
    async getSectionItemList(params: { [key: string]: any } = {}) {
      const service = await this.getSectionItemService();
      return await service?.getMany(params, {
        pageSize: -1,
      });
    },
    async getSubSectionList(sectionUid: string) {
      const result = await this.getSectionItemList({ section_uid: sectionUid });
      const list = result?.list || [];
      this.sectionItemMap[sectionUid] = list as ISectionItem[];
      return this.sectionItemMap[sectionUid];
    },
    async changeToTakeOut(uid: string) {
      const service = await this.getSectionItemService();
      service.changeToTakeOut(uid);
    },
    async toggleLock(item: ISectionItem) {
      const service = await this.getSectionItemService();
      await service.updateOne(item.id_in_server!, { ...item, is_locked: !item.is_locked });
    },
    async changeSequence(item: Section, sequence: number) {
      const miscStore = useMiscStore();
      const service = await miscStore.getAppMiscService();
      const sectionService = await this.getSectionService();
      return service.changeSequence<ISection>(sectionService!, item, sequence);
    },
  },
});
